import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../services/supabaseClient';
import { useAuth } from './AuthContext';

interface Shop {
  id: string;
  name: string;
  slug: string;
  logo_url?: string;
  banner_url?: string;
  description?: string;
  status: string;
}

interface ShopContextType {
  activeShop: Shop | null;
  setActiveShopId: (id: string | null) => void;
  shops: Shop[];
  loading: boolean;
  refreshShops: () => Promise<void>;
}

const ShopContext = createContext<ShopContextType | undefined>(undefined);

export const ShopProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [activeShop, setActiveShop] = useState<Shop | null>(null);
  const [shops, setShops] = useState<Shop[]>([]);
  const [loading, setLoading] = useState(true);

  const refreshShops = async () => {
    if (!user) return;

    setLoading(true);
    // Explicitly type the data from Supabase to avoid implicit 'any'.
    const { data, error } = await supabase
      .from('shops')
      .select('*')
      .eq('owner_id', user.id);

    if (!error && data) {
      setShops(data as Shop[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    refreshShops();
  }, [user]);

  const setActiveShopId = (id: string | null) => {
    if (!id) {
      setActiveShop(null);
      localStorage.removeItem('activeShopId');
    } else {
      const selected = shops.find(s => s.id === id);
      if (selected) {
        setActiveShop(selected);
        localStorage.setItem('activeShopId', id);
      }
    }
  };

  // Restore active shop from local storage
  useEffect(() => {
    const savedId = localStorage.getItem('activeShopId');
    if (savedId && shops.length > 0) {
      const selected = shops.find(s => s.id === savedId);
      if (selected) setActiveShop(selected);
    }
  }, [shops]);

  return (
    <ShopContext.Provider value={{ activeShop, setActiveShopId, shops, loading, refreshShops }}>
      {children}
    </ShopContext.Provider>
  );
};

export const useShop = () => {
  const context = useContext(ShopContext);
  if (context === undefined) {
    throw new Error('useShop must be used within a ShopProvider');
  }
  return context;
};
